import axios from 'axios';

import { detect } from 'detect-browser';
import { Account } from '@/models/account';
import cache, { del } from '@/helpers/cache';
import { BillingAccount } from '@/models/billingAccount';
const browser = detect();
const browserUID = (() => {
  const nav = window.navigator;
  const screen = window.screen;
  let guid = String(nav.mimeTypes.length);
  guid += nav.userAgent.replace(/\D+/g, '');
  guid += nav.plugins.length;
  guid += screen.height || '';
  guid += screen.width || '';
  guid += screen.pixelDepth || '';
  return guid;
})();

// auth http client using firebase token
const bAccounts = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  withCredentials: true,

  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
  data: {
    device_uid: browserUID,
    device_type: 'browser',
    device_platform: browser ? browser.os : 'unknown',
    device_os_version: (browser ? browser.name : 'unknown') + '_' + (browser ? browser.version : 'unknown'),
  },
});

const dataOnly = (v: any) => v.data.data;
let debounceListAccounts: any = 0;
let debounceListAccountsIDS: any = [];
const debounceListAccountsCalls: any = [];

const service = {
  http: bAccounts,

  setToken(token: string) {
    this.http.defaults.headers.Authorization = 'token ' + token;
  },

  async login(data?: object | null) {
    data = data || {};
    try {
      const res = await this.http.post('/account/login', data);
      return res.data.data;
    } catch (err) {
      throw err;
    }
  },

  async searchAccounts(search: string) {
    const data = {
      search
    };
    try {
      const res = await this.http.post('/admin/account/search', data);
      return res.data.data;
    } catch (err) {
      throw err;
    }
  },

  async searchBillingAccounts(search: string) {
    const data = {
      search
    };
    try {
      const res = await this.http.post('/admin/billing_account/search', data);
      return res.data.data;
    } catch (err) {
      throw err;
    }
  },

  async CreateBillingAccount(req: BillingAccount): Promise<BillingAccount> {
    return dataOnly(await this.http.post('/admin/billing_account/create', req));
  },

  async UpdateBillingAccount(req: BillingAccount) {
    return dataOnly(await this.http.post('/admin/billing_account/update', req));
  },

  // async searchXeroAccounts(search: string) {
  //   const data = {
  //     search
  //   };
  //   try {
  //     const res = await this.http.post('/admin/xero_account/search', data);
  //     return res.data.data;
  //   } catch (err) {
  //     throw err;
  //   }
  // },

  async ListAccountsByIDs(ids: number[]) {
    return dataOnly(await this.http.post('/admin/account/by_ids', { ids }));
  },

  async listSalesAgents() {
    return dataOnly(await this.http.get('/admin/account/sales_agents'));
  },


  async _GetAccountByID(id: number) {
    if (debounceListAccounts) { clearTimeout(debounceListAccounts); }
    debounceListAccounts = setTimeout(() => {
      debounceListAccountsIDS = debounceListAccountsIDS.filter(
        (v: any, idx: any, self: any) => {
          return self.indexOf(v) === idx;
        });
      this.ListAccountsByIDs(debounceListAccountsIDS).then(((data) => {
        for (const ob of debounceListAccountsCalls) {
          ob.resolve(data);
        }
        debounceListAccountsIDS = [];
      }));
    }, 200);

    // dedup multiple calls with same caching key
    debounceListAccountsIDS.push(id);
    const waitFnc = new Promise((resolve, reject) => {
      debounceListAccountsCalls.push({
        resolve: (data: any) => {
          resolve(data.find((acc: Account) => acc.id === id));
        }, reject
      });
    });
    return await waitFnc;
  },

  // must add debounce to limit calls to back-end
  async GetAccountByID(id: number) {
    const key = `account_${id}`;
    // cache accounts found for 15min
    return await cache(key, 60 * 15, async () => await this._GetAccountByID(id));
  },

  async GetAccountsByPermission(
    permissionName: string,
    readPermission?: boolean,
    writePermission?: boolean,
    deletePermission?: boolean
  ) {
    const key = `account_perm_${permissionName}_${readPermission ? 1 : 0}`
      + `${writePermission ? 1 : 0}${deletePermission ? 1 : 0}`;
    return await cache(key, 60 * 15, async () => {
      return dataOnly(
        await this.http.post('/admin/account/by_permission', {
          permissionName,
          r: readPermission,
          w: writePermission,
          d: deletePermission
        })
      );
    });
  },

  async RegisterAccount(req: Account) {
    return dataOnly(await this.http.post('/admin/account/create', req));
  },

  async UpdateAccount(req: Account) {
    del(`account_${req.id}`); // deleta account cache
    return dataOnly(await this.http.post('/admin/account/update', req));
  }
};

export default service;
